import React from 'react'

import { FormattedMessage } from 'react-intl'
import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import InfluencerIdentityRowLabelTooltip from '@components/InfluencerIdentity/Row/LabelTooltip'
import InfluencerIdentityRowValueAndLabel from '@components/InfluencerIdentity/Row/ValueAndLabel'
import Price from '@components/UI/Price/Price'
import { useInfluencerIdentityId } from '@context/InfluencerIdentityId/Provider'
import { IApplicationState } from '@store'
import { selectIsPriceHidden, selectIsUnlockedCampaign } from '@store/campaigns'
import {
  selectInfluencerIdentityEstimatedCPE,
  selectInfluencerIdentityEstimatedCPECurrency,
  selectInfluencerIdentityEstimatedCPEIsAverage,
} from '@store/influencerIdentities'
import { selectCampaignId } from '@store/router'
import { Container } from '@tribegroup'
import { EstimatedCPETooltipID } from './Tooltip/AverageTooltip'

interface IInternalProps {
  currency: string
  cpe: number
  isAveraged: boolean
}

const CPEValue = ({ isAveraged, currency, cpe }: IInternalProps) => (
  <Container inlineFlex alignItems="center" gap={0.125} noWrap>
    <Price currency={currency} quantity={cpe} pattern="!###,###," />
    {isAveraged && (
      <Container data-tooltip-id={EstimatedCPETooltipID} data-for={EstimatedCPETooltipID} pointer>
        <FormattedMessage id="core.text.symbol.attention" />
      </Container>
    )}
  </Container>
)

const InfluencerIdentityRowPerformanceEstimatedCPE = () => {
  const match = useRouteMatch()

  const { contextInfluencerIdentityId } = useInfluencerIdentityId()
  const { cpe, currency, isAveraged, isPriceHidden } = useSelector((state: IApplicationState) => {
    const campaignId = selectCampaignId(match)!
    return {
      cpe: selectInfluencerIdentityEstimatedCPE(state, campaignId, contextInfluencerIdentityId),
      currency: selectInfluencerIdentityEstimatedCPECurrency(
        state,
        campaignId,
        contextInfluencerIdentityId,
      ),
      isAveraged: selectInfluencerIdentityEstimatedCPEIsAverage(
        state,
        campaignId,
        contextInfluencerIdentityId,
      ),
      isPriceHidden:
        selectIsPriceHidden(state, campaignId) || selectIsUnlockedCampaign(state, campaignId),
    }
  }, shallowEqual)

  if (isPriceHidden) {
    return null
  }

  return (
    <InfluencerIdentityRowValueAndLabel
      value={
        <React.Fragment>
          {cpe ? (
            <CPEValue currency={currency} cpe={cpe} isAveraged={isAveraged} />
          ) : (
            <FormattedMessage id="core.metric.empty" />
          )}
        </React.Fragment>
      }
      label={
        <InfluencerIdentityRowLabelTooltip
          waypointOffset={350}
          title={<FormattedMessage id="submission.performance.estimatedCpe" />}
          body={
            <FormattedMessage
              id={'submission.performance.estimatedCpe.body'}
              values={{ p: (chunks) => <p>{chunks}</p>, br: () => <br /> }}
            />
          }
        >
          <FormattedMessage id="submission.performance.estCpe" />
        </InfluencerIdentityRowLabelTooltip>
      }
    />
  )
}

export default InfluencerIdentityRowPerformanceEstimatedCPE
